// What a figure counts, or why it is Unavailable, in a small pop-up. The page keeps putting these in
// `title` attributes; this moves each to data-why (no slow native tooltip on top) and adds a small "?"
// beside it, the only thing that opens the pop-up: on hover, keyboard focus or tap. Passing the mouse
// over the figure itself opens nothing. Leaving the "?", Escape or a tap elsewhere hides it.

const MARGIN = 16; // keep the pop-up this far inside the window
const GAP = 6; // between the "?" and the pop-up's arrow tip
const CIRCLE = 25; // the widest "?" circle (a chart's, with its ring): the circle is always the button's right end
// A grid, flex or table row would take the "?" as one more cell: it goes in the row's first text instead.
const ROW_LIKE = /^(inline-)?(grid|flex)$|^table-row$/;

export function explainOnHover() {
  const tip = document.createElement('div');
  tip.id = 'why-tip';
  tip.className = 'why-tip';
  tip.setAttribute('role', 'tooltip');
  tip.hidden = true;
  document.body.append(tip);
  const marks = new WeakMap<HTMLElement, HTMLButtonElement>(); // an element with a reason → its "?"
  const owners = new WeakMap<HTMLButtonElement, HTMLElement>();
  let current: HTMLButtonElement | null = null;
  let focused: HTMLButtonElement | null = null; // the "?" with keyboard focus, kept through a re-render
  let dismissed: HTMLButtonElement | null = null; // a focused "?" whose pop-up Escape closed
  let refocusing = false; // focus given back after a re-render, not a new focus by the user

  const hide = () => {
    current?.removeAttribute('aria-describedby');
    current = null;
    tip.hidden = true;
  };
  // Just below the "?", or above when there is no room; never past the window's edges. The arrow stays
  // on the "?" even when the box has to shift sideways.
  const show = (mark: HTMLButtonElement) => {
    const text = owners.get(mark)?.dataset.why ?? '';
    if (!text || !mark.isConnected) {
      hide();
      return;
    }
    current?.removeAttribute('aria-describedby');
    current = mark;
    mark.setAttribute('aria-describedby', tip.id);
    tip.textContent = text;
    tip.hidden = false;
    // Measure at the corner: a box near the right edge wraps narrower and taller, which would skew the fit.
    tip.style.left = '0px';
    tip.style.top = '0px';
    const r = mark.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const x = r.right - Math.min(r.width, CIRCLE) / 2; // the circle's centre, also beside a chart's "Unavailable"
    const left = Math.max(MARGIN, Math.min(x - t.width / 2, window.innerWidth - MARGIN - t.width));
    const fitsBelow = r.bottom + GAP + t.height <= window.innerHeight - MARGIN;
    tip.classList.toggle('above', !fitsBelow);
    tip.style.left = `${left}px`;
    tip.style.top = `${fitsBelow ? r.bottom + GAP : Math.max(MARGIN, r.top - GAP - t.height)}px`;
    tip.style.setProperty('--arrow-x', `${Math.max(12, Math.min(x - left, t.width - 12))}px`);
  };

  // Where an element's "?" goes: at the end of its text. A select's goes in its label.
  const firstText = (el: HTMLElement) =>
    [...el.children].find(
      (c): c is HTMLElement => c instanceof HTMLElement && !c.classList.contains('why-q') && !!c.textContent?.trim(),
    );
  const spotOf = (el: HTMLElement): { at: HTMLElement; after: boolean } => {
    if (el instanceof HTMLSelectElement) {
      const label = el.id ? document.querySelector<HTMLElement>(`label[for="${CSS.escape(el.id)}"]`) : null;
      return label ? { at: label, after: false } : { at: el, after: true };
    }
    let at = el;
    if (ROW_LIKE.test(getComputedStyle(el).display)) {
      for (let next = firstText(at); next; next = firstText(at)) {
        at = next;
      }
    }
    return { at, after: false };
  };
  // Takes over an element's title and keeps its "?" in place, as the page writes and rewrites them.
  const sync = (node: Node) => {
    if (!(node instanceof HTMLElement) || node.classList.contains('why-q')) {
      return;
    }
    if (node.hasAttribute('title')) {
      node.dataset.why = node.getAttribute('title') ?? '';
      node.removeAttribute('title');
    }
    if (node.dataset.why === undefined) {
      return;
    }
    let mark = marks.get(node);
    if (!node.dataset.why) {
      mark?.remove(); // nothing to explain: no "?"
      if (mark === focused) {
        focused = null;
      }
      return;
    }
    if (!mark) {
      mark = document.createElement('button');
      mark.type = 'button';
      mark.className = 'why-q';
      mark.setAttribute('aria-label', 'What this means');
      marks.set(node, mark);
      owners.set(mark, node);
    }
    const { at, after } = spotOf(node);
    // In place when nothing but other "?"s follow it (two in one spot must not keep swapping).
    let next = mark.nextSibling;
    while (next instanceof Element && next.classList.contains('why-q')) {
      next = next.nextSibling;
    }
    if (after ? at.nextSibling !== mark : mark.parentNode !== at || next !== null) {
      if (after) {
        at.after(mark);
      } else {
        at.append(mark);
      }
    }
    if (mark === focused && (document.activeElement === document.body || !document.activeElement)) {
      // A refresh rewrote its element: focus stays, and its pop-up too unless Escape had closed it.
      refocusing = true;
      mark.focus({ preventScroll: true });
      refocusing = false;
    } else if (mark === current) {
      show(mark); // a new reason replaces the one shown
    }
  };

  const markOf = (node: EventTarget | null) =>
    node instanceof Element ? node.closest<HTMLButtonElement>('.why-q') : null;
  // A finger opens it with a tap (the click) only: a swipe that starts on a "?" is a scroll.
  document.addEventListener('pointerover', (e) => {
    const mark = markOf(e.target);
    if (e.pointerType !== 'touch' && mark && mark !== current) {
      dismissed = null;
      show(mark);
    }
  });
  document.addEventListener('pointerout', (e) => {
    if (
      e.pointerType !== 'touch' &&
      current &&
      markOf(e.target) === current &&
      !(e.relatedTarget instanceof Node && current.contains(e.relatedTarget))
    ) {
      hide();
    }
  });
  document.addEventListener('focusin', (e) => {
    const mark = markOf(e.target);
    // Kept only for the keyboard: a "?" a mouse clicked stays closed after a refresh.
    focused = mark?.matches(':focus-visible') ? mark : null;
    if (!refocusing) {
      dismissed = null;
    }
    if (mark && !(refocusing && mark === dismissed)) {
      show(mark);
    } else {
      hide();
    }
  });
  document.addEventListener('focusout', (e) => {
    const mark = markOf(e.target);
    if (mark && mark === focused) {
      // Moved on, or re-rendered away? A removal blurs before the element has left, and the re-render
      // puts the "?" back (and focuses it) before this runs: forget it only if focus went elsewhere.
      setTimeout(() => {
        if (focused === mark && document.activeElement !== mark) {
          focused = null;
        }
      });
    }
    hide();
  });
  // A tap on a "?" opens it and nothing else (not a link, label or row it sits in); a tap elsewhere closes it.
  document.addEventListener(
    'click',
    (e) => {
      const mark = markOf(e.target);
      if (mark) {
        e.preventDefault();
        e.stopPropagation();
        dismissed = null;
        show(mark);
      } else {
        hide();
      }
    },
    { capture: true },
  );
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      dismissed = current ?? dismissed; // a second Escape, with nothing open, keeps the first one's
      hide();
    }
  });
  // Scrolling moves the "?" under the pop-up: it follows.
  window.addEventListener(
    'scroll',
    () => {
      if (current) {
        show(current);
      }
    },
    { capture: true, passive: true },
  );

  for (const el of document.querySelectorAll('[title]')) {
    sync(el);
  }
  new MutationObserver((changes) => {
    for (const c of changes) {
      if (c.type === 'attributes') {
        sync(c.target);
        continue;
      }
      for (const n of c.addedNodes) {
        if (n instanceof Element) {
          sync(n);
          for (const el of n.querySelectorAll('[title], [data-why]')) {
            sync(el);
          }
        }
      }
      // A rewrite of an element's text (or of the part holding its "?") drops the "?": put it back.
      const owner = c.target instanceof Element ? c.target.closest('[data-why]') : null;
      if (owner) {
        sync(owner);
      }
    }
    // Its element was replaced by a re-render: nothing left to point at.
    if (current && !current.isConnected) {
      hide();
    }
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['title'] });
}
